// src/pages/customer-panel/support/CreateTicketPage.js
// Create Support Ticket Page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const PRIORITY_OPTIONS = [
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'CRITICAL', label: 'Critical' },
];

export default function CreateTicketPage() {
    const navigate = useNavigate();

    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('MEDIUM');
    const [appointmentId, setAppointmentId] = useState('');
    const [appointments, setAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(true);
    const [invoiceId, setInvoiceId] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        document.title = 'Create Ticket | Customer Panel';
        fetchAppointments();
        fetchInvoices();
    }, []);

    const fetchAppointments = async () => {
        setLoadingAppointments(true);
        try {
            const response = await axiosInstance.get('/api/appointments/my-dropdown');
            const data = response.data;
            setAppointments(data.data || data.appointments || data || []);
        } catch (err) {
            console.error('Failed to fetch appointments:', err);
            // Non-blocking -- appointments are optional
            setAppointments([]);
        } finally {
            setLoadingAppointments(false);
        }
    };

    const fetchInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const response = await axiosInstance.get('/api/invoices/my-dropdown');
            const data = response.data;
            setInvoices(data.data || []);
        } catch (err) {
            console.error('Failed to fetch invoices:', err);
            setInvoices([]);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subject.trim()) {
            setError('Subject is required.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const payload = {
                subject: subject.trim(),
                description: description.trim(),
                priority,
            };
            if (appointmentId) {
                payload.appointment_id = appointmentId;
            }
            if (invoiceId) {
                payload.invoice_id = invoiceId;
            }
            await axiosInstance.post('/api/customer/tickets', payload);
            setSnackbar({ open: true, message: 'Ticket created successfully.', severity: 'success' });
            setTimeout(() => {
                navigate('/customer-panel/support');
            }, 800);
        } catch (err) {
            console.error('Failed to create ticket:', err);
            setError(err.response?.data?.message || 'Failed to create ticket. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const fieldSx = {
        mb: 3,
        '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
        '& .MuiInputLabel-root': { fontFamily },
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
            {/* Back Button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/customer-panel/support')}
                sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontWeight: 600, mb: 3 }}
            >
                Back to Tickets
            </Button>

            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: '#1e293b',
                        fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                >
                    Create Support Ticket
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                    Describe your issue and we will get back to you as soon as possible
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            <Paper
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    p: { xs: 2, md: 3 },
                }}
            >
                {/* Subject */}
                <TextField
                    label="Subject"
                    required
                    fullWidth
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary of your issue"
                    sx={fieldSx}
                />

                {/* Description */}
                <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide details about your issue..."
                    sx={fieldSx}
                />

                {/* Priority */}
                <TextField
                    label="Priority"
                    select
                    fullWidth
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    sx={fieldSx}
                >
                    {PRIORITY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Appointment (optional) */}
                <TextField
                    label="Link to Appointment (optional)"
                    select
                    fullWidth
                    value={appointmentId}
                    onChange={(e) => setAppointmentId(e.target.value)}
                    disabled={loadingAppointments}
                    sx={fieldSx}
                    SelectProps={{
                        displayEmpty: true,
                    }}
                >
                    <MenuItem value="" sx={{ fontFamily, color: '#94a3b8' }}>
                        {loadingAppointments ? 'Loading appointments...' : 'None'}
                    </MenuItem>
                    {appointments.map((appt) => (
                        <MenuItem key={appt.id || appt._id} value={appt.id || appt._id} sx={{ fontFamily }}>
                            {appt.title || appt.name || appt.label || `Appointment #${appt.id || appt._id}`}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Invoice (optional) */}
                <TextField
                    label="Link to Invoice (optional)"
                    select
                    fullWidth
                    value={invoiceId}
                    onChange={(e) => setInvoiceId(e.target.value)}
                    disabled={loadingInvoices}
                    sx={fieldSx}
                    SelectProps={{
                        displayEmpty: true,
                    }}
                >
                    <MenuItem value="" sx={{ fontFamily, color: '#94a3b8' }}>
                        {loadingInvoices ? 'Loading invoices...' : 'None'}
                    </MenuItem>
                    {invoices.map((inv) => (
                        <MenuItem key={inv.id} value={inv.id} sx={{ fontFamily }}>
                            {inv.invoice_number} - ${inv.amount} ({inv.status})
                        </MenuItem>
                    ))}
                </TextField>

                {/* Submit */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/customer-panel/support')}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            color: '#64748b',
                            borderColor: '#e2e8f0',
                            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                            px: 3,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        endIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />}
                        disabled={submitting || !subject.trim()}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            bgcolor: GREEN_ACCENT,
                            '&:hover': { bgcolor: '#059669' },
                            px: 3,
                        }}
                    >
                        {submitting ? 'Creating...' : 'Create Ticket'}
                    </Button>
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
